// useLeaseDraft — the workhorse React hook for §1.
//
// Responsibilities:
//   - Load the draft on mount (GET → 404 → POST to create).
//   - Hold the canonical LeaseFormState; expose setState so the form can
//     mutate it locally.
//   - Debounced autosave (500ms after last change; 5s max-wait per §1.4).
//   - 30s heartbeat interval while editable (§1.6) — suspended after 60 min
//     of inactivity, re-proven on return (see "idle heartbeat suspension").
//   - 5s lock-state polling when in State C — only while the tab is in use.
//   - beforeunload release (fetch keepalive: true; §1.6).
//   - Heartbeat 409 → transition to lock-stolen state with banner.
//   - Compute the page-state selector (A / B / C / D).
//
// State transitions (page-level):
//
//   ┌──────────────────────────────────────────────────────────────────────┐
//   │ load draft (GET → 404 → POST)                                        │
//   │                                                                      │
//   │   draft loaded                                                       │
//   │     │                                                                │
//   │     ▼                                                                │
//   │   group archived? ──yes──► State D (read-only; versions visible)     │
//   │     │ no                                                             │
//   │     ▼                                                                │
//   │   lock held by other? ──yes──► State C (modal: Wait / Take over)     │
//   │     │ no                                                             │
//   │     ▼                                                                │
//   │   inputsHash === lastGeneratedInputsHash? ──yes──► State B (locked   │
//   │     │ no                                          form; Edit Draft;  │
//   │     ▼                                             Download PDF)      │
//   │   State A (editable; live preview; Generate Lease)                   │
//   │                                                                      │
//   │ Any heartbeat 409 → "lock stolen" sub-state of C (banner + read-only)│
//   └──────────────────────────────────────────────────────────────────────┘
//
// All async errors surface via `saveStatus = 'error'` (the form-level
// indicator) or a structured `lockBanner` field; the hook never throws to
// the React render tree.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as drafts from '@/lib/leaseDraftsClient';
import { ApiError } from '@/lib/api';
import {
  createActivePoller,
  createBarrier,
  createIdleSuspender,
  getActivityGate,
  type ActivePoller,
  type ActivityWatch,
  type Barrier,
  type IdleSuspender,
} from '@/lib/activityGate';
import { useAuth } from '@/contexts/AuthContext';
import type {
  ApplicationGroupShape,
  LeaseFormState,
} from '@/lib/leaseConfig';
import type {
  DraftLockState,
  LeaseDraftRecord,
  LeasePdfVersionRecord,
  LeaseKickoffState,
} from '@/lib/leaseDraftsClient';

// ---------------------------------------------------------------------------
// Constants (§1.4 / §1.6)
// ---------------------------------------------------------------------------

const AUTOSAVE_DEBOUNCE_MS = 500;
const AUTOSAVE_MAX_WAIT_MS = 5_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const LOCK_POLL_INTERVAL_MS = 5_000;

/**
 * Aurora load workstream #4: an abandoned builder tab must not heartbeat
 * forever. The heartbeat stops once the tab has been inactive this long — no
 * input for 60 min, or hidden for 60 min. Deliberately far longer than the
 * 15-min default: people read long leases without typing.
 */
export const HEARTBEAT_IDLE_WATCH: ActivityWatch = {
  idleMs: 60 * 60_000,
  hiddenGraceMs: 60 * 60_000,
};

// ---------------------------------------------------------------------------
// Public hook types
// ---------------------------------------------------------------------------

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export type PageState = 'A' | 'B' | 'C' | 'D' | 'loading' | 'not-found';

export interface UseLeaseDraftResult {
  /** Current draft record. Null while loading or after an unrecoverable error. */
  draft: LeaseDraftRecord | null;
  /** Current form state (mirror of `draft.state` after the last save). */
  state: LeaseFormState | null;
  /** AppFolio group attached to the draft (re-fetched on every read). */
  group: ApplicationGroupShape | null;
  /** Page-level state machine selector. */
  pageState: PageState;
  /** Save indicator state for the header. */
  saveStatus: SaveStatus;
  /** Timestamp of last successful save (for "Saved 2:14 PM"). */
  lastSavedAt: Date | null;
  /** Last inputsHash returned from PATCH (§1.4 step 4). */
  inputsHash: string | null;
  /** WEL-44: set when the draft load detected + reconciled a property move. */
  propertyMove: { changes: string[]; newUnitLabel?: string } | null;
  /** Sticky banner content; null when no banner. */
  lockBanner: LockBanner | null;
  /** Number of consecutive autosave failures (§1.4). */
  saveErrorCount: number;
  /** All generated PDF versions (newest first). */
  versions: LeasePdfVersionRecord[];
  /** Generate in progress flag (button should be disabled). */
  generating: boolean;
  /** True iff the form should accept edits (State A or "B-with-Edit"). */
  editable: boolean;
  /** True iff user clicked "Edit Draft" in State B. */
  editingFromStateB: boolean;
  /** Server-provided flag; absent/false keeps the staff kickoff UI dormant. */
  leaseKickoffEnabled: boolean;
  /** Live kickoff snapshot returned with the existing draft response. */
  leaseKickoff: LeaseKickoffState | null;

  // ---- actions ----

  /** Apply a local form-state update; schedules autosave. */
  setState: (
    next: LeaseFormState | ((prev: LeaseFormState) => LeaseFormState),
  ) => void;
  /** Flush any pending autosave immediately. Used before generate. */
  flushSave: () => Promise<void>;
  /** Manually retry the last failed save. */
  retrySave: () => Promise<void>;
  /** Manually transition State B → State A. */
  enterEditMode: () => void;
  /** Wait sub-state (close lock modal; keep polling). */
  waitForLockRelease: () => void;
  /** Take-over the lock; transitions to State A. */
  takeoverLock: () => Promise<void>;
  /** Generate a new PDF version; refreshes versions list. Returns a tagged
   * result. On `ccr-error`, the body carries hoaSource/hoaId/link so the UI
   * can render a "Fix this here" affordance per §8.7 (Phase E Codex S2). */
  generate: () => Promise<GenerateResult>;
  /** Refresh the versions list. */
  refreshVersions: () => Promise<void>;
  /** Re-read the builder snapshot after an external staff action. */
  refreshDraft: () => Promise<void>;
  /**
   * WEL-169: reload ONLY the AppFolio group (+ the server's staleness hash and
   * property-move notice) after an AppFolio refresh changed the mirror. The form
   * state is deliberately untouched, so an edit typed while the refresh was in
   * flight is never overwritten; the preview and summary read the property's
   * AppFolio fields from the group at render time.
   */
  refreshGroup: () => Promise<boolean>;
}

export type GenerateResult =
  | { kind: 'success'; version: LeasePdfVersionRecord; downloadUrl: string }
  | { kind: 'no-group' }
  | { kind: 'unknown-error' }
  | {
      /**
       * The designation hard-block fired (409 `needsDesignation:true`, only when
       * INTAKE_ENFORCE_DESIGNATION is on). The lease can't generate until a
       * resident designation is on file — either collected via intake or entered
       * by staff in the builder's Resident designation section. `message` is the
       * server's actionable reason.
       */
      kind: 'needs-designation';
      message: string;
    }
  | {
      kind: 'ccr-error';
      status: 409 | 422;
      message: string;
      hoaSource?: 'property-associated';
      hoaId: string | null;
      errorKind?: 'append-failed' | 's3-read-failed';
      link: string | null;
    };

export interface LockBanner {
  kind: 'stolen' | 'archived' | 'not-on-active-list';
  message: string;
  /** When kind === 'stolen': who took over. */
  holder?: string | null;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

// Lock TTL must mirror the server-side constant in apps/api/src/lib/leaseDrafts.ts.
// Used by computeAndSetPageState to detect "lock held by someone else with a
// fresh heartbeat" — the cold-mount State C path (Opus C1 review B2).
const LOCK_TTL_MS = 120 * 1000;

function lockHeldByOtherUser(
  lock: LeaseDraftRecord['lock'] | undefined | null,
  myUserSub: string | undefined,
): boolean {
  if (!lock) return false;
  if (!myUserSub) return false;
  if (lock.holder == null) return false;
  if (lock.holder === myUserSub) return false;
  // Lock is held by someone else. Is the heartbeat fresh?
  const hb = lock.lastHeartbeatAt;
  const gen = lock.generationLockUntil;
  const now = Date.now();
  if (hb && Date.parse(hb) + LOCK_TTL_MS > now) return true;
  if (gen && Date.parse(gen) > now) return true;
  return false;
}

export function useLeaseDraft(groupId: string | undefined): UseLeaseDraftResult {
  const { user } = useAuth();
  const userSub = user?.sub;

  // ---- core state ----
  const [draft, setDraftRecord] = useState<LeaseDraftRecord | null>(null);
  const [state, setStateRaw] = useState<LeaseFormState | null>(null);
  const [group, setGroup] = useState<ApplicationGroupShape | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [inputsHash, setInputsHash] = useState<string | null>(null);
  const [propertyMove, setPropertyMove] = useState<
    { changes: string[]; newUnitLabel?: string } | null
  >(null);
  const [lockBanner, setLockBanner] = useState<LockBanner | null>(null);
  const [saveErrorCount, setSaveErrorCount] = useState(0);
  const [versions, setVersions] = useState<LeasePdfVersionRecord[]>([]);
  const [generating, setGenerating] = useState(false);
  const [editingFromStateB, setEditingFromStateB] = useState(false);
  const [leaseKickoffEnabled, setLeaseKickoffEnabled] = useState(false);
  const [leaseKickoff, setLeaseKickoff] = useState<LeaseKickoffState | null>(null);

  // ---- refs (mutable state that should not trigger re-renders) ----

  const debounceRef = useRef<number | null>(null);
  const maxWaitRef = useRef<number | null>(null);
  const pendingStateRef = useRef<LeaseFormState | null>(null);
  const lastSavedStateRef = useRef<LeaseFormState | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ActivePoller | null>(null);
  const lockStolenRef = useRef(false);
  const unmountedRef = useRef(false);
  // Idle heartbeat suspension (see the section below). `heartbeatSuspended`
  // gates the heartbeat effect; the barrier holds every write (autosave,
  // generate) from the moment the heartbeat is suspended until the lock is
  // re-proven — or found stolen — on return.
  const [heartbeatSuspended, setHeartbeatSuspended] = useState(false);
  const lockBarrierRef = useRef<Barrier | null>(null);
  const suspenderRef = useRef<IdleSuspender | null>(null);
  const draftRef = useRef<LeaseDraftRecord | null>(null);
  draftRef.current = draft;

  // ---- helpers ----

  const clearDebounce = useCallback(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (maxWaitRef.current !== null) {
      window.clearTimeout(maxWaitRef.current);
      maxWaitRef.current = null;
    }
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current !== null) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const stopLockPoll = useCallback(() => {
    if (pollTimerRef.current !== null) {
      pollTimerRef.current.stop();
      pollTimerRef.current = null;
    }
  }, []);

  // ---- mount: load draft ----
  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    unmountedRef.current = false;
    setPageState('loading');
    (async () => {
      try {
        let result = await drafts.getDraft(groupId);
        if (cancelled) return;
        if (!result) {
          // 404 — create. Server fetches group + buildDefaultFormState.
          const created = await drafts.createDraft(groupId);
          if (cancelled) return;
          // Re-GET so we get the live group attached for State A/B math.
          result = await drafts.getDraft(groupId);
          if (cancelled || !result) {
            void created;
            return;
          }
        }
        setDraftRecord(result.draft);
        setStateRaw(result.draft.state);
        setGroup(result.group);
        setPropertyMove(result.propertyMove ?? null);
        setLeaseKickoffEnabled(result.leaseKickoffEnabled === true);
        setLeaseKickoff(result.leaseKickoff ?? null);
        lastSavedStateRef.current = result.draft.state;
        // Per Opus C1 review B3: seed inputsHash from the server's GET
        // response so the State A/B selector can reach State B on cold mount
        // when the draft hasn't been edited since the last generate. The
        // server-side getDraft response includes inputsHash (computed against
        // the live group via hashLeaseInputs). The client doesn't recompute
        // this locally because safe-stable-stringify isn't wired client-side
        // — the server is the source of truth for inputsHash on cold load.
        if (result.inputsHash !== undefined) {
          setInputsHash(result.inputsHash);
        }
        // Compute initial pageState from the loaded record + seeded hash.
        computeAndSetPageState(
          result.draft,
          result.group,
          /*editingFromB*/ false,
          result.inputsHash ?? null,
        );
      } catch (err) {
        if (cancelled) return;
        // Hard load failure — set saveStatus to error and leave pageState
        // loading; the ConfigurePage error UI will show a generic message.
        setSaveStatus('error');
        console.warn('useLeaseDraft.load.failed', err);
      }
    })();
    return () => {
      cancelled = true;
      unmountedRef.current = true;
      clearDebounce();
      stopHeartbeat();
      stopLockPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // ---- compute pageState selector ----

  const computeAndSetPageState = useCallback(
    (
      d: LeaseDraftRecord | null,
      g: ApplicationGroupShape | null,
      editingFromB: boolean,
      currentInputsHash: string | null,
    ) => {
      if (!d) {
        setPageState(g === null ? 'not-found' : 'loading');
        return;
      }
      if (d.status === 'archived') {
        setPageState('D');
        return;
      }
      // Lock-stolen sub-state of C — handled via lockBanner; keep pageState
      // === 'C' so the form is read-only.
      if (lockStolenRef.current) {
        setPageState('C');
        return;
      }
      // §1.10 criterion 1: cold-mount State C. If the server-side lock is held
      // by another user with a fresh heartbeat (or an active generation lock),
      // the page is read-only and the LockModal opens. Without this, U2
      // landing on a URL U1 is editing would land in State A, type, autosave,
      // and only THEN get a misleading "lock stolen" banner — without ever
      // having held the lock. Opus C1 review B2.
      if (lockHeldByOtherUser(d.lock, userSub)) {
        setPageState('C');
        return;
      }
      // §1.10 criterion 5: State B requires the live inputsHash (from the
      // server's last PATCH/GET response) to match the draft's
      // lastGeneratedInputsHash. The server is the only source of truth for
      // inputsHash on cold load — the client doesn't have safe-stable-stringify
      // wired. Opus C1 review B3.
      const hasGenerated = d.latestGeneratedVersionId !== null;
      const matchesLast =
        hasGenerated
        && currentInputsHash !== null
        && d.lastGeneratedInputsHash === currentInputsHash;
      if (matchesLast && !editingFromB) {
        setPageState('B');
        return;
      }
      setPageState('A');
    },
    [userSub],
  );

  // ---- re-evaluate pageState whenever inputs change ----

  useEffect(() => {
    if (!draft) return;
    computeAndSetPageState(draft, group, editingFromStateB, inputsHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, group, inputsHash, editingFromStateB, userSub]);

  // ---- save (debounced; max-wait; called by setState) ----

  const performSave = useCallback(
    async (next: LeaseFormState) => {
      if (!groupId) return;
      if (lockStolenRef.current) return; // do not write after losing the lock
      clearDebounce();
      setSaveStatus('saving');
      // Returning from an idle heartbeat suspension: the lock has to be
      // re-proven before anything is written — a PATCH implicitly acquires a
      // free/stale lock, which would overwrite edits someone else made while
      // this tab sat idle. Never set for an active editor.
      const barrier = lockBarrierRef.current;
      if (barrier) {
        const proceed = await barrier.wait;
        if (!proceed || unmountedRef.current || lockStolenRef.current) return;
        // A later edit queued its own save while we waited; that one writes.
        if (pendingStateRef.current !== null && pendingStateRef.current !== next) return;
      }
      try {
        const res = await drafts.patchDraft(groupId, next);
        if (unmountedRef.current) return;
        setDraftRecord(res.draft);
        lastSavedStateRef.current = res.draft.state;
        setInputsHash(res.inputsHash);
        setSaveStatus('saved');
        setLastSavedAt(new Date());
        setSaveErrorCount(0);
      } catch (err) {
        if (unmountedRef.current) return;
        if (err instanceof ApiError && err.status === 409) {
          // Lock stolen mid-edit.
          handleStolenLock(err.body);
        } else {
          setSaveStatus('error');
          setSaveErrorCount((c) => c + 1);
        }
        console.warn('useLeaseDraft.save.failed', err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupId],
  );

  const handleStolenLock = useCallback((body: unknown) => {
    lockStolenRef.current = true;
    const lock =
      typeof body === 'object' && body !== null && 'lock' in (body as Record<string, unknown>)
        ? (body as { lock: DraftLockState | null }).lock
        : null;
    const holderDisplay = lock?.holderDisplay ?? 'another user';
    setLockBanner({
      kind: 'stolen',
      message: `You no longer hold the lock. ${holderDisplay} took over. Your saved changes are preserved.`,
      holder: holderDisplay,
    });
    setPageState('C');
    setSaveStatus('idle');
    stopHeartbeat();
    clearDebounce();
  }, [stopHeartbeat, clearDebounce]);

  const scheduleSave = useCallback(
    (next: LeaseFormState) => {
      pendingStateRef.current = next;
      // Debounce: cancel + restart 500ms timer.
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        if (maxWaitRef.current !== null) {
          window.clearTimeout(maxWaitRef.current);
          maxWaitRef.current = null;
        }
        if (pendingStateRef.current) {
          void performSave(pendingStateRef.current);
        }
      }, AUTOSAVE_DEBOUNCE_MS);
      // Max-wait: only schedule if not already pending.
      if (maxWaitRef.current === null) {
        maxWaitRef.current = window.setTimeout(() => {
          maxWaitRef.current = null;
          if (debounceRef.current !== null) {
            window.clearTimeout(debounceRef.current);
            debounceRef.current = null;
          }
          if (pendingStateRef.current) {
            void performSave(pendingStateRef.current);
          }
        }, AUTOSAVE_MAX_WAIT_MS);
      }
    },
    [performSave],
  );

  // ---- setState — public mutator ----

  const setState = useCallback(
    (next: LeaseFormState | ((prev: LeaseFormState) => LeaseFormState)) => {
      setStateRaw((prev) => {
        if (prev === null) return prev;
        const nextValue = typeof next === 'function' ? (next as (p: LeaseFormState) => LeaseFormState)(prev) : next;
        scheduleSave(nextValue);
        // Transitioning out of State B: any edit reveals the form.
        if (editingFromStateB === false && draft && draft.latestGeneratedVersionId !== null) {
          // No explicit flag flip needed — pageState re-evaluates against
          // inputsHash on save response. But State A should be active
          // immediately so the form is editable now.
          // The 'editable' selector below already treats State A as editable.
        }
        return nextValue;
      });
    },
    [scheduleSave, editingFromStateB, draft],
  );

  const flushSave = useCallback(async () => {
    if (debounceRef.current !== null || maxWaitRef.current !== null) {
      clearDebounce();
      if (pendingStateRef.current) {
        await performSave(pendingStateRef.current);
      }
    }
  }, [clearDebounce, performSave]);

  const retrySave = useCallback(async () => {
    if (pendingStateRef.current) {
      await performSave(pendingStateRef.current);
    } else if (state) {
      await performSave(state);
    }
  }, [performSave, state]);

  const enterEditMode = useCallback(() => {
    setEditingFromStateB(true);
    setPageState('A');
  }, []);

  // ---- heartbeat (30s while State A/B-with-Edit) ----

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    if (!groupId) return;
    heartbeatTimerRef.current = window.setInterval(async () => {
      try {
        const res = await drafts.heartbeatLock(groupId);
        if (unmountedRef.current) return;
        setDraftRecord(res.draft);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          handleStolenLock(err.body);
        } else {
          console.warn('useLeaseDraft.heartbeat.failed', err);
        }
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [groupId, stopHeartbeat, handleStolenLock]);

  // Start/stop heartbeat based on editable state. Acquire the lock first if
  // we don't already hold it — without this, a fresh draft has an empty lock
  // (createDraft initializes lock fields to null) and the first heartbeat
  // returns 409 (`lock.holder = :me` fails because holder is still NULL).
  // The pageState machine puts us in State A as long as no OTHER user holds
  // the lock, but doesn't imply we hold it ourselves; this effect closes
  // that gap.
  useEffect(() => {
    if (!draft || pageState === 'loading' || pageState === 'not-found') return;
    if (pageState !== 'A') {
      stopHeartbeat();
      return;
    }
    if (!groupId) return;
    // Idle-suspended: no acquire, no heartbeat until the return re-proves the lock.
    if (heartbeatSuspended) {
      stopHeartbeat();
      return;
    }

    let cancelled = false;
    (async () => {
      // If we're not the current holder (NULL holder, or someone else's sub
      // that pageState-deemed-stale), reaffirm. acquireLock is idempotent for
      // the holder=me case — server condition admits `holder = :me`.
      if (draft.lock.holder !== userSub) {
        try {
          const res = await drafts.acquireLock(groupId);
          if (cancelled || unmountedRef.current) return;
          setDraftRecord(res.draft);
        } catch (err) {
          if (cancelled || unmountedRef.current) return;
          // 409 means someone genuinely raced in; stolen-lock path handles it.
          if (err instanceof ApiError && err.status === 409) {
            handleStolenLock(err.body);
            return;
          }
          console.warn('useLeaseDraft.acquire-on-mount.failed', err);
          // Fall through to start heartbeat anyway; subsequent 409 will be
          // handled by the normal stolen-lock flow.
        }
      }
      if (cancelled || unmountedRef.current) return;
      startHeartbeat();
      // Started after the 60-min idle transition went by (it can't suspend a
      // heartbeat that wasn't running yet)? Suspend it now.
      suspenderRef.current?.checkNow();
    })();

    return () => {
      cancelled = true;
      stopHeartbeat();
    };
  }, [pageState, draft, startHeartbeat, stopHeartbeat, groupId, userSub, handleStolenLock, heartbeatSuspended]);

  // ---- idle heartbeat suspension (Aurora load workstream #4) ----
  //
  // An abandoned builder tab used to heartbeat (and re-list versions on every
  // heartbeat's draft update) all night. Once the tab has been inactive for
  // HEARTBEAT_IDLE_WATCH (60 min without input, or hidden 60 min) while it is
  // heartbeating, the heartbeat stops and every write is held. The server lock
  // then goes stale after its 120 s TTL, exactly as if the tab had closed
  // without a beforeunload release.
  //
  // On return the lock is re-proven through the EXISTING takeover acquire,
  // fingerprinted with this tab's own holder + last heartbeat: it succeeds only
  // if nobody touched the lock meanwhile (holder AND lastHeartbeatAt unchanged
  // since our last heartbeat). Then the heartbeat resumes and held writes go
  // through. If anyone acquired it in the meantime — even if they have since
  // released it, and may have edited — the 409 lands in the existing
  // "lock stolen" state (banner + read-only; reload to continue), so this tab's
  // stale form can never overwrite their work. A network/5xx failure keeps the
  // writes held and retries on the heartbeat cadence while the user is here.
  // Active editors never reach any of this.

  const verifyInFlightRef = useRef(false);
  const verifyRetryRef = useRef<number | null>(null);

  /** proceed = the lock was re-proven (held writes go through); false = lost. */
  const finishIdleResume = useCallback((proceed: boolean) => {
    suspenderRef.current?.resolve();
    setHeartbeatSuspended(false);
    const barrier = lockBarrierRef.current;
    lockBarrierRef.current = null;
    barrier?.release(proceed);
  }, []);

  const verifyLockAfterIdle = useCallback(async (): Promise<void> => {
    if (!groupId || verifyInFlightRef.current) return;
    if (verifyRetryRef.current !== null) {
      window.clearTimeout(verifyRetryRef.current);
      verifyRetryRef.current = null;
    }
    const lock = draftRef.current?.lock;
    const previousHolder = lock?.holder ?? userSub ?? '';
    if (!previousHolder) {
      // Nothing to fingerprint the lock with — it can't be re-proven.
      handleStolenLock(null);
      finishIdleResume(false);
      return;
    }
    verifyInFlightRef.current = true;
    try {
      const res = await drafts.acquireLock(groupId, {
        previousHolder,
        previousHeartbeatAt: lock?.lastHeartbeatAt ?? null,
      });
      if (unmountedRef.current) return;
      setDraftRecord(res.draft); // holder = me, fresh heartbeat → effect restarts it
      finishIdleResume(true);
    } catch (err) {
      if (unmountedRef.current) return;
      // 409: someone took the lock while we were away. Any other definitive
      // 4xx (the draft was discarded, access revoked) also means the lock
      // can't be re-proven. Stolen first (so held writes see lockStolenRef),
      // then release them. 401 (after the client's own refresh-retry), 408,
      // 429, 5xx and network errors are transient: retry below.
      const definitive =
        err instanceof ApiError
        && err.status >= 400
        && err.status < 500
        && err.status !== 401
        && err.status !== 408
        && err.status !== 429;
      if (definitive) {
        handleStolenLock((err as ApiError).body);
        finishIdleResume(false);
        return;
      }
      console.warn('useLeaseDraft.idle-resume.failed', err);
      verifyRetryRef.current = window.setTimeout(() => {
        verifyRetryRef.current = null;
        if (getActivityGate().isActive(HEARTBEAT_IDLE_WATCH)) void verifyLockAfterIdleRef.current();
      }, HEARTBEAT_INTERVAL_MS);
    } finally {
      verifyInFlightRef.current = false;
    }
  }, [groupId, userSub, handleStolenLock, finishIdleResume]);

  const verifyLockAfterIdleRef = useRef(verifyLockAfterIdle);
  verifyLockAfterIdleRef.current = verifyLockAfterIdle;

  useEffect(() => {
    if (!groupId) return;
    const suspender = createIdleSuspender({
      gate: getActivityGate(),
      watch: HEARTBEAT_IDLE_WATCH,
      // Only an editor that is actually heartbeating holds anything to let go.
      canSuspend: () => heartbeatTimerRef.current !== null && !lockStolenRef.current,
      onSuspend: () => {
        lockBarrierRef.current = createBarrier();
        stopHeartbeat();
        setHeartbeatSuspended(true);
      },
      onResume: () => void verifyLockAfterIdleRef.current(),
    });
    suspenderRef.current = suspender;
    return () => {
      suspender.stop();
      suspenderRef.current = null;
      if (verifyRetryRef.current !== null) {
        window.clearTimeout(verifyRetryRef.current);
        verifyRetryRef.current = null;
      }
      setHeartbeatSuspended(false);
      const barrier = lockBarrierRef.current;
      lockBarrierRef.current = null;
      barrier?.release(false); // unmount / draft switch: held writes are abandoned
    };
  }, [groupId, stopHeartbeat]);

  // ---- lock polling (5s while State C without stolen flag) ----

  // Only while the tab is in use (Aurora load workstream #4): paused while
  // hidden / 15 min without input; on return it polls at once if a poll came
  // due meanwhile, then resumes the 5 s cadence.
  const startLockPoll = useCallback(() => {
    stopLockPoll();
    if (!groupId) return;
    pollTimerRef.current = createActivePoller({
      gate: getActivityGate(),
      intervalMs: LOCK_POLL_INTERVAL_MS,
      runOnStart: false,
      run: async () => {
      try {
        const { lock } = await drafts.getLock(groupId);
        if (unmountedRef.current) return;
        // Phase C Codex S1: attempt acquire whenever the lock is NOT held by
        // another live user — either no holder at all, or the heartbeat/gen
        // window has expired and the lock is reclaimable. Without the
        // staleness branch, polling never auto-recovered after the holder
        // crashed (no beforeunload fired) — staff had to refresh the page.
        if (!lock.holder || !lockHeldByOtherUser(lock, userSub)) {
          try {
            const res = await drafts.acquireLock(groupId);
            setDraftRecord(res.draft);
            stopLockPoll();
            lockStolenRef.current = false;
            setLockBanner(null);
          } catch {
            /* still held (server-side condition fresher than our snapshot); keep polling */
          }
        } else {
          // Update draft.lock state for the modal display.
          setDraftRecord((d) => (d ? { ...d, lock } : d));
        }
      } catch (err) {
        console.warn('useLeaseDraft.lockPoll.failed', err);
      }
      },
    });
  }, [groupId, stopLockPoll, userSub]);

  // Decide whether to poll for the lock when in State C (but not stolen-sub).
  useEffect(() => {
    if (pageState === 'C' && !lockStolenRef.current) {
      startLockPoll();
    } else {
      stopLockPoll();
    }
    return () => stopLockPoll();
  }, [pageState, startLockPoll, stopLockPoll]);

  // ---- beforeunload release ----

  useEffect(() => {
    if (!groupId) return;
    const handler = () => {
      // Always attempt release on unload — backend tolerates non-holder
      // (idempotent). flushSave is skipped here: keepalive can carry small
      // payloads but a PATCH may exceed limits; the autosave 5s max-wait
      // ensures we're rarely more than 5s behind.
      void drafts.releaseLockOnUnload(groupId);
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [groupId]);

  // ---- take-over lock ----

  const waitForLockRelease = useCallback(() => {
    // No-op: pageState stays C; polling already active.
    // The modal close handler in the consumer calls this to keep semantics
    // explicit.
  }, []);

  const takeoverLock = useCallback(async () => {
    if (!groupId || !draft) return;
    try {
      const res = await drafts.acquireLock(groupId, {
        previousHolder: draft.lock.holder ?? '',
        previousHeartbeatAt: draft.lock.lastHeartbeatAt,
      });
      setDraftRecord(res.draft);
      lockStolenRef.current = false;
      setLockBanner(null);
      // Transition to A.
      setPageState('A');
    } catch (err) {
      console.warn('useLeaseDraft.takeover.failed', err);
      // 409: refresh lock state and let the user decide.
      try {
        const { lock } = await drafts.getLock(groupId);
        setDraftRecord((d) => (d ? { ...d, lock } : d));
      } catch {
        /* ignore */
      }
    }
  }, [groupId, draft]);

  // ---- generate ----

  const generate = useCallback(async () => {
    if (!groupId) return { kind: 'no-group' as const };
    setGenerating(true);
    try {
      // Returning from an idle heartbeat suspension: generate implicitly
      // acquires the lock server-side, so wait until this tab has re-proven it
      // — a PDF built from edits someone else made meanwhile must not go out
      // under this screen. Never set for an active editor.
      const barrier = lockBarrierRef.current;
      if (barrier) {
        const proceed = await barrier.wait;
        if (!proceed || unmountedRef.current || lockStolenRef.current) {
          console.warn('useLeaseDraft.generate.skipped: lock lost while idle');
          return { kind: 'unknown-error' as const };
        }
      }
      // Flush any pending save first (§1.9).
      await flushSave();
      const res = await drafts.generate(groupId);
      // Refresh draft + versions.
      const reloaded = await drafts.getDraft(groupId);
      if (reloaded) {
        setDraftRecord(reloaded.draft);
        setGroup(reloaded.group);
        // Recompute inputsHash with the live group post-generate so the
        // selector returns State B immediately (no edits since generate).
        setInputsHash(reloaded.draft.lastGeneratedInputsHash);
        setEditingFromStateB(false);
      }
      // Refresh versions list.
      const vlist = await drafts.listVersions(groupId);
      setVersions(vlist.versions);
      return { kind: 'success' as const, version: res.version, downloadUrl: res.downloadUrl };
    } catch (err) {
      console.warn('useLeaseDraft.generate.failed', err);
      // Phase E Codex S2: surface structured CC&R errors so the UI can render
      // a targeted message + "Fix this here" link from the API's 409/422 body.
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) {
        const body = err.body as {
          error?: string;
          needsDesignation?: boolean;
          hoaSource?: 'property-associated';
          hoaId?: string | null;
          kind?: 'append-failed' | 's3-read-failed';
          link?: string | null;
        } | null;
        // Designation hard-block (INTAKE_ENFORCE_DESIGNATION). Handled before the
        // generic CC&R branch because this 409 also carries `error` but no
        // hoaId/link — the ConfigurePage surfaces a dedicated inline hint that
        // points staff to the Resident designation section.
        if (body?.needsDesignation) {
          return {
            kind: 'needs-designation' as const,
            message:
              body.error ??
              'A resident designation is required before this lease can be generated.',
          };
        }
        if (body?.error) {
          const result: GenerateResult = {
            kind: 'ccr-error',
            status: err.status as 409 | 422,
            message: body.error,
            hoaId: body.hoaId ?? null,
            link: body.link ?? null,
            ...(body.hoaSource !== undefined ? { hoaSource: body.hoaSource } : {}),
            ...(body.kind !== undefined ? { errorKind: body.kind } : {}),
          };
          return result;
        }
      }
      return { kind: 'unknown-error' as const };
    } finally {
      setGenerating(false);
    }
  }, [groupId, flushSave]);

  const refreshVersions = useCallback(async () => {
    if (!groupId) return;
    try {
      const vlist = await drafts.listVersions(groupId);
      setVersions(vlist.versions);
    } catch (err) {
      console.warn('useLeaseDraft.versions.failed', err);
    }
  }, [groupId]);

  const refreshDraft = useCallback(async () => {
    if (!groupId) return;
    try {
      const reloaded = await drafts.getDraft(groupId);
      if (!reloaded || unmountedRef.current) return;
      setDraftRecord(reloaded.draft);
      setStateRaw(reloaded.draft.state);
      setGroup(reloaded.group);
      setInputsHash(reloaded.inputsHash ?? null);
      setPropertyMove(reloaded.propertyMove ?? null);
      setLeaseKickoffEnabled(reloaded.leaseKickoffEnabled === true);
      setLeaseKickoff(reloaded.leaseKickoff ?? null);
      lastSavedStateRef.current = reloaded.draft.state;
    } catch (err) {
      console.warn('useLeaseDraft.refreshDraft.failed', err);
    }
  }, [groupId]);

  const refreshGroup = useCallback(async (): Promise<boolean> => {
    if (!groupId) return false;
    try {
      const reloaded = await drafts.getDraft(groupId);
      if (!reloaded || unmountedRef.current) return false;
      setGroup(reloaded.group);
      setInputsHash(reloaded.inputsHash ?? null);
      setPropertyMove(reloaded.propertyMove ?? null);
      return true;
    } catch (err) {
      console.warn('useLeaseDraft.refreshGroup.failed', err);
      return false;
    }
  }, [groupId]);

  // Load versions whenever the draft loads.
  useEffect(() => {
    if (!groupId || !draft) return;
    void refreshVersions();
  }, [groupId, draft, refreshVersions]);

  // ---- editable selector ----

  const editable = useMemo(() => {
    if (!draft) return false;
    if (draft.status === 'archived') return false;
    if (lockStolenRef.current) return false;
    if (pageState === 'C') return false;
    if (pageState === 'D') return false;
    if (pageState === 'B') return false;
    return true;
  }, [draft, pageState]);

  return {
    draft,
    state,
    group,
    pageState,
    saveStatus,
    lastSavedAt,
    inputsHash,
    propertyMove,
    lockBanner,
    saveErrorCount,
    versions,
    generating,
    editable,
    editingFromStateB,
    leaseKickoffEnabled,
    leaseKickoff,
    setState,
    flushSave,
    retrySave,
    enterEditMode,
    waitForLockRelease,
    takeoverLock,
    generate,
    refreshVersions,
    refreshDraft,
    refreshGroup,
  };
}
